import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, Loader, Save } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import { REGISTER_STEPS } from './aiModelConstants';
import { createDefaultForm, firstErrorMessage, modelToForm, regenerateExamples, slugifyCode, validateForSave, validateStep } from './aiModelForm';
import { logAiModel } from './aiModelLogger';
import { buildInvokeContract, prettyJson } from './aiModelSchema';
import { aiModelService } from './aiModelService';
import { AiModelFormState, AiModelStatus } from './aiModelTypes';
import { btnPrimary, btnSecondary, chipClass, SectionCard } from './aiModelUi';
import ExamplesStep from './steps/ExamplesStep';
import InferenceModeStep from './steps/InferenceModeStep';
import InputContractStep from './steps/InputContractStep';
import ModelInfoStep from './steps/ModelInfoStep';
import ModelTypeStep from './steps/ModelTypeStep';
import OutputContractStep from './steps/OutputContractStep';
import PreprocessingStep from './steps/PreprocessingStep';
import ReviewStep from './steps/ReviewStep';
import SourceConnectionStep from './steps/SourceConnectionStep';
import TestStep from './steps/TestStep';

const AiModelRegisterScreen: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const navigate = useNavigate();
  const { modelId } = useParams<{ modelId: string }>();
  const isEdit = Boolean(modelId);
  const [form, setForm] = useState<AiModelFormState>(() => createDefaultForm());
  const [codeTouched, setCodeTouched] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [loadingModel, setLoadingModel] = useState(Boolean(modelId));
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!modelId) {
      logAiModel('app', 'register_screen_open');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingModel(true);
      try {
        const existing = await aiModelService.getById(modelId);
        if (cancelled) return;
        setForm(modelToForm(existing));
        setCodeTouched(true);
        logAiModel('app', 'edit_screen_open', { id: existing.id, code: existing.code });
      } catch (err) {
        if (cancelled) return;
        logAiModel('error', 'edit_model_missing', { id: modelId, message: err instanceof Error ? err.message : 'missing' });
        setMissing(true);
      } finally {
        if (!cancelled) setLoadingModel(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  const invokePreview = useMemo(() => prettyJson(buildInvokeContract(form)), [form]);

  const patch = (update: Partial<AiModelFormState>) => {
    if (update.code !== undefined) setCodeTouched(true);
    setForm((prev) => ({ ...prev, ...update }));
    setErrors({});
    setBanner(null);
  };

  const handleNameChange = (name: string) => {
    setForm((prev) => ({
      ...prev,
      name,
      code: codeTouched ? prev.code : slugifyCode(name),
    }));
    setErrors({});
  };

  const goNext = () => {
    const stepErrors = validateStep(activeSection, form);
    if (Object.keys(stepErrors).length) {
      setErrors(stepErrors);
      setBanner(firstErrorMessage(stepErrors));
      logAiModel('warning', 'step_validation_failed', { step: REGISTER_STEPS[activeSection], errors: stepErrors });
      return;
    }
    if (activeSection === 3 || activeSection === 4) {
      const examples = regenerateExamples(form);
      if (form.sampleRequest.trim() === '{\n  \n}' || form.sampleResponse.trim() === '{\n  \n}') {
        setForm((prev) => ({ ...prev, ...examples }));
      }
    }
    setActiveSection((index) => Math.min(index + 1, REGISTER_STEPS.length - 1));
  };

  const goPrev = () => {
    if (activeSection === 0) {
      navigate('/portal/ai-models');
      return;
    }
    setActiveSection((index) => index - 1);
    setErrors({});
    setBanner(null);
  };

  const handleRunTest = async () => {
    setTesting(true);
    setBanner(null);
    try {
      const result = isEdit && modelId
        ? await aiModelService.testSaved(modelId)
        : await aiModelService.testDraft(form);
      setForm((prev) => ({ ...prev, lastTest: result }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Test failed';
      logAiModel('error', 'test_model_failed', { message });
      setBanner(message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (status: AiModelStatus) => {
    const saveErrors = validateForSave(form);
    if (Object.keys(saveErrors).length) {
      setErrors(saveErrors);
      setBanner(firstErrorMessage(saveErrors));
      logAiModel('warning', 'save_validation_failed', { status, errors: saveErrors });
      return;
    }
    if (status === 'registered' && !form.lastTest) {
      logAiModel('warning', 'register_without_test', { code: form.code });
    }
    setSaving(true);
    try {
      if (isEdit && modelId) {
        await aiModelService.update(modelId, form, status);
      } else {
        await aiModelService.create(form, status);
      }
      navigate('/portal/ai-models');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save model';
      logAiModel('error', 'save_model_failed', { message, status, code: form.code });
      setBanner(message);
    } finally {
      setSaving(false);
    }
  };

  const stepProps = { form, isLight, errors, patch };

  const renderSection = () => {
    switch (activeSection) {
      case 0:
        return <ModelInfoStep {...stepProps} onNameChange={handleNameChange} />;
      case 1:
        return <SourceConnectionStep {...stepProps} />;
      case 2:
        return <ModelTypeStep {...stepProps} />;
      case 3:
        return <InputContractStep {...stepProps} />;
      case 4:
        return <OutputContractStep {...stepProps} />;
      case 5:
        return <PreprocessingStep {...stepProps} />;
      case 6:
        return <InferenceModeStep {...stepProps} />;
      case 7:
        return <ExamplesStep {...stepProps} />;
      case 8:
        return <TestStep {...stepProps} testing={testing} onRunTest={() => void handleRunTest()} />;
      default:
        return <ReviewStep {...stepProps} />;
    }
  };

  if (isEdit && loadingModel && !missing) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <Loader size={32} className="mx-auto mb-3 text-indigo-400 animate-spin" />
        <p className={`text-sm ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Loading model…</p>
      </div>
    );
  }

  if (missing) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h1 className={`text-lg font-semibold mb-2 ${isLight ? 'text-neutral-900' : 'text-white'}`}>Model not found</h1>
        <p className={`text-sm mb-4 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
          This model was not found for this tenant.
        </p>
        <button type="button" onClick={() => navigate('/portal/ai-models')} className={btnPrimary()}>
          Back to AI Models
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
            {isEdit ? 'Edit AI model' : 'Register AI model'}
          </h1>
          <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
            Capture the invoke contract now so workflows can bind this model later.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => handleSave('draft')} disabled={saving} className={btnSecondary(isLight)}>
            <FileText size={14} /> Save draft
          </button>
          <button type="button" onClick={() => handleSave('registered')} disabled={saving} className={btnPrimary(saving)}>
            <Save size={14} /> {saving ? 'Saving…' : isEdit ? 'Save' : 'Register'}
          </button>
        </div>
      </div>

      {banner && (
        <div
          className={`rounded-lg border px-3 py-2 text-xs ${
            isLight ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-amber-500/20 bg-amber-500/10 text-amber-200'
          }`}
        >
          {banner}
        </div>
      )}

      <div className="flex gap-4">
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap gap-1">
            {REGISTER_STEPS.map((section, i) => (
              <button
                key={section}
                type="button"
                onClick={() => {
                  if (i >= 7) {
                    const emptyRequest = form.sampleRequest.trim() === '{\n  \n}' || !form.sampleRequest.trim();
                    const emptyResponse = form.sampleResponse.trim() === '{\n  \n}' || !form.sampleResponse.trim();
                    if (emptyRequest || emptyResponse) {
                      setForm((prev) => ({ ...prev, ...regenerateExamples(prev) }));
                    }
                  }
                  setActiveSection(i);
                  setErrors({});
                  setBanner(null);
                }}
                className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${chipClass(
                  activeSection === i,
                  isLight
                )}`}
              >
                {i + 1}. {section}
              </button>
            ))}
          </div>

          <SectionCard
            title={REGISTER_STEPS[activeSection]}
            description={`Section ${activeSection + 1} of ${REGISTER_STEPS.length}`}
            isLight={isLight}
          >
            {renderSection()}
          </SectionCard>

          <div className="flex items-center justify-between">
            <button type="button" onClick={goPrev} className={btnSecondary(isLight)}>
              <ChevronLeft className="w-3.5 h-3.5" /> {activeSection === 0 ? 'Cancel' : 'Previous'}
            </button>
            {activeSection < REGISTER_STEPS.length - 1 ? (
              <button type="button" onClick={goNext} className={btnPrimary()}>
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button type="button" onClick={() => handleSave('registered')} disabled={saving} className={btnPrimary(saving)}>
                {isEdit ? 'Save model' : 'Register model'}
              </button>
            )}
          </div>
        </div>

        <div className="w-80 shrink-0 hidden lg:block">
          <div className="sticky top-4">
            <div
              className={`rounded-xl overflow-hidden border ${
                isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'
              }`}
            >
              <div
                className={`px-3 py-2 border-b flex items-center justify-between ${
                  isLight ? 'border-neutral-100 bg-neutral-50' : 'border-neutral-800 bg-neutral-900'
                }`}
              >
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
                  Invoke contract
                </span>
                <span className="text-[10px] font-medium text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded">Live</span>
              </div>
              <pre
                className={`p-3 text-[10px] font-mono overflow-auto max-h-[calc(100vh-12rem)] leading-relaxed ${
                  isLight ? 'text-neutral-700 bg-neutral-50/80' : 'text-gray-300 bg-neutral-950'
                }`}
              >
                {invokePreview}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiModelRegisterScreen;
